import type {
  StepButtonConfig,
  ResolvedWizardButtons,
  StepSnapshot,
  WizardPhase,
  WizardCondition,
} from './types';
import type { WizardStepButtons } from '../../types/blocks';

export interface DeriveButtonContext {
  wizardButtons?: StepButtonConfig | WizardStepButtons;
  stepButtons?: StepButtonConfig | WizardStepButtons;
  stepIndex: number;
  totalSteps: number;
  phase: WizardPhase;
  snapshot: StepSnapshot;
  hasCancelHandler: boolean;
  /** Current wizard mode (create/edit) — drives default disable-until-dirty for edit */
  mode?: string;
  /** Wizard meta bag — used for enabledWhen condition evaluation */
  meta?: Record<string, unknown>;
}

function normalizePrimary(
  btns?: StepButtonConfig | WizardStepButtons,
): StepButtonConfig['primary'] {
  if (!btns) return undefined;
  const asSB = btns as WizardStepButtons;
  if (asSB.next) return { label: asSB.next.label };
  return (btns as StepButtonConfig).primary;
}

/**
 * Evaluates a WizardCondition against a flat context bag.
 * Used for `enabledWhen` on buttons.
 */
function evaluateCondition(
  condition: WizardCondition,
  context: Record<string, unknown>,
): boolean {
  const value = resolveFieldPath(condition.field, context);
  const op = condition.op ?? 'truthy';

  switch (op) {
    case 'eq':
      return value === condition.value;
    case 'neq':
      return value !== condition.value;
    case 'truthy':
      return !!value;
    case 'falsy':
      return !value;
    case 'notEmpty':
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'string') return value.length > 0;
      return value != null;
    default:
      return true;
  }
}

function resolveFieldPath(path: string, obj: Record<string, unknown>): unknown {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/**
 * Pure function that merges wizard-level and step-level button configs,
 * applies snapshot-derived state, and returns resolved button state
 * ready for rendering.
 *
 * Default behaviors (no JSON config needed):
 * - Buttons disabled during handler/transition phase
 * - Cancel shows confirm when form is dirty
 * - Edit mode: primary disabled until form is dirty
 *
 * Declarative overrides:
 * - `enabledWhen.condition` — custom condition evaluated against snapshot + meta
 */
export function deriveButtonState(ctx: DeriveButtonContext): ResolvedWizardButtons {
  const wizPrimary = normalizePrimary(ctx.wizardButtons);
  const stepPrimary = normalizePrimary(ctx.stepButtons);

  const merged: StepButtonConfig = {
    ...ctx.wizardButtons,
    ...ctx.stepButtons,
    primary: { ...wizPrimary, ...stepPrimary },
    back: { ...(ctx.wizardButtons as StepButtonConfig)?.back, ...(ctx.stepButtons as StepButtonConfig)?.back },
    cancel: { ...(ctx.wizardButtons as StepButtonConfig)?.cancel, ...(ctx.stepButtons as StepButtonConfig)?.cancel },
    secondary: (ctx.stepButtons as StepButtonConfig)?.secondary ?? (ctx.wizardButtons as StepButtonConfig)?.secondary ?? [],
    enabledWhen: (ctx.stepButtons as StepButtonConfig)?.enabledWhen ?? (ctx.wizardButtons as StepButtonConfig)?.enabledWhen,
  };

  const isFirst = ctx.stepIndex === 0;
  const isLast = ctx.stepIndex >= ctx.totalSteps - 1;
  const isBusy =
    ctx.phase === 'handler_running' || ctx.phase === 'transitioning';

  // Edit mode: primary stays disabled until form is dirty (default behavior)
  const isEditMode = ctx.mode === 'edit';
  const disabledUntilDirty = isEditMode && !ctx.snapshot.isDirty;

  // Custom enabledWhen condition evaluation
  let conditionBlocked = false;
  if (merged.enabledWhen?.condition) {
    const conditionContext: Record<string, unknown> = {
      snapshot: ctx.snapshot,
      meta: ctx.meta ?? {},
      isDirty: ctx.snapshot.isDirty,
      isLoading: ctx.snapshot.isLoading,
      isSubmitting: ctx.snapshot.isSubmitting,
      ready: ctx.snapshot.ready,
      ...ctx.snapshot.customState,
    };
    conditionBlocked = !evaluateCondition(merged.enabledWhen.condition, conditionContext);
  }

  const primaryDisabled = isBusy || disabledUntilDirty || conditionBlocked;

  return {
    primary: {
      label: merged.primary?.label ?? (isLast ? 'Submit' : 'Next'),
      disabled: primaryDisabled,
      loading: isBusy,
      hidden: merged.primary?.hidden === true,
    },
    back: {
      label: merged.back?.label ?? 'Back',
      disabled: isBusy,
      hidden: isFirst || merged.back?.hidden === true,
    },
    cancel: {
      label: merged.cancel?.label ?? 'Cancel',
      disabled: isBusy,
      hidden: !ctx.hasCancelHandler || merged.cancel?.hidden === true,
      confirmRequired: ctx.snapshot.isDirty && merged.cancel?.skipConfirm !== true,
    },
    secondary: (merged.secondary ?? []).map((btn) => ({
      id: btn.id,
      label: btn.label,
      disabled: isBusy,
      loading: false,
      variant: btn.variant ?? 'secondary',
      visible: true,
    })),
  };
}
